/**
 * Enhanced Router Service
 * 
 * Provides utilities for hash-based routing with state synchronization.
 * Following dev guide: hash routing for static hosting compatibility.
 */

// Route interface
export interface Route {
  page: string;
  id?: string;
  params?: Record<string, string>;
}

// Route parsing and navigation utilities
export class Router {
  private static instance: Router;
  private listeners: Array<(route: Route) => void> = [];

  private constructor() {
    this.setupHashListener();
  }

  static getInstance(): Router {
    if (!Router.instance) {
      Router.instance = new Router();
    }
    return Router.instance;
  }

  /**
   * Parse hash into route object
   */
  parseRoute(hash: string = window.location.hash): Route {
    // Remove # and leading slash
    const path = hash.replace(/^#\/?/, '');
    
    if (!path) {
      return { page: 'landing' };
    }

    // Split path and query params
    const [pathPart, queryPart] = path.split('?');
    const segments = pathPart.split('/').filter(Boolean);
    
    const route: Route = {
      page: segments[0] || 'landing',
      id: segments[1]
    };

    // Parse query parameters
    if (queryPart) {
      const params: Record<string, string> = {};
      const searchParams = new URLSearchParams(queryPart);
      searchParams.forEach((value, key) => {
        params[key] = value;
      });
      route.params = params;
    }

    return route;
  }

  /**
   * Get current route
   */
  getCurrentRoute(): Route {
    return this.parseRoute();
  }

  /**
   * Navigate to a new route
   */
  navigate(page: string, id?: string, params?: Record<string, string>): void {
    let hash = `#/${page}`;
    
    if (id) {
      hash += `/${id}`;
    }
    
    if (params && Object.keys(params).length > 0) {
      const queryString = new URLSearchParams(params).toString();
      hash += `?${queryString}`;
    }

    window.location.hash = hash;
  }

  /**
   * Navigate to landing page
   */
  navigateToLanding(): void {
    this.navigate('landing');
  }

  /**
   * Navigate to group page
   */
  navigateToGroup(groupId: string, params?: Record<string, string>): void {
    this.navigate('group', groupId, params);
  }

  /**
   * Navigate to export page
   */
  navigateToExport(groupId: string): void {
    this.navigate('export', groupId);
  }

  /**
   * Navigate to error page
   */
  navigateToError(errorCode?: string): void {
    const params = errorCode ? { code: errorCode } : undefined;
    this.navigate('error', undefined, params);
  }

  /**
   * Replace current route without adding to history
   */
  replace(page: string, id?: string, params?: Record<string, string>): void {
    let hash = `#/${page}`;
    
    if (id) {
      hash += `/${id}`;
    }
    
    if (params && Object.keys(params).length > 0) {
      const queryString = new URLSearchParams(params).toString();
      hash += `?${queryString}`;
    }

    window.location.replace(window.location.pathname + hash);
  }

  /**
   * Add route change listener
   */
  addListener(callback: (route: Route) => void): () => void {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Get shareable URL for current group (basic ID-only version)
   * For full sharing with data, use generateShareUrl from shareLink.ts
   */
  getShareableUrl(groupId: string): string {
    const baseUrl = `${window.location.origin}${window.location.pathname}`;
    return `${baseUrl}#/group/${groupId}`;
  }

  /**
   * Check if we're on a specific page
   */
  isCurrentPage(page: string): boolean {
    return this.getCurrentRoute().page === page;
  }

  /**
   * Get current group ID if on group page
   */
  getCurrentGroupId(): string | null {
    const route = this.getCurrentRoute();
    return route.page === 'group' ? route.id || null : null;
  }

  /**
   * Setup hash change listener
   */
  private setupHashListener(): void {
    window.addEventListener('hashchange', () => {
      const route = this.getCurrentRoute();
      this.listeners.forEach(callback => callback(route));
    });
  }

  /**
   * Validate route parameters
   */
  validateRoute(route: Route): { valid: boolean; error?: string } {
    // Basic route validation
    if (!route.page) {
      return { valid: false, error: 'Page is required' };
    }

    // Validate specific pages
    switch (route.page) {
      case 'group':
        if (!route.id) {
          return { valid: false, error: 'Group ID is required for group page' };
        }
        if (!/^group-\d+-[a-z0-9]+$/.test(route.id)) {
          return { valid: false, error: 'Invalid group ID format' };
        }
        break;
      
      case 'export':
        if (!route.id) {
          return { valid: false, error: 'Group ID is required for export page' };
        }
        break;
    }

    return { valid: true };
  }
}

// Export singleton instance
export const router = Router.getInstance();

// Convenience functions
export const navigate = (page: string, id?: string, params?: Record<string, string>) => 
  router.navigate(page, id, params);

export const navigateToLanding = () => router.navigateToLanding();
export const navigateToGroup = (groupId: string) => router.navigateToGroup(groupId);
export const navigateToExport = (groupId: string) => router.navigateToExport(groupId);
export const navigateToError = (errorCode?: string) => router.navigateToError(errorCode);

export const getCurrentRoute = () => router.getCurrentRoute();
export const getCurrentGroupId = () => router.getCurrentGroupId();
export const getShareableUrl = (groupId: string) => router.getShareableUrl(groupId);
